/**
 * The environments of the current tenant and the one the app is looking at (docs/environments.md, "UI").
 * The URL (`?env=`) is the shared state: an environment named by a link is adopted, an in-app link that
 * lost it gets it back, and switching is a navigation to the same page with the other `env`. The subtree
 * is keyed by tenant and environment, so a switch drops every piece of state and fetches everything again.
 */
import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { Api } from './api.ts';
import { envOfSearch, getEnvId, resolveEnv, searchWithEnv, setEnvId, showSwitcher, subscribeEnv, switchTarget, withEnv, type Environment } from './env.ts';
import { Spinner } from '../components/ui.tsx';

interface EnvState {
  envs: Environment[];
  /** The environment in use: the chosen one, else the tenant's default; null before there is a list. */
  current: Environment | null;
  /** The environment the URLs carry; null until one was chosen. */
  explicitId: string | null;
  /** Whether the switcher is worth showing. */
  visible: boolean;
  select: (envId: string) => void;
  /** An in-app path with the current environment in its query. */
  link: (to: string) => string;
  reload: () => void;
}

const NONE: EnvState = { envs: [], current: null, explicitId: null, visible: false, select: () => undefined, link: (to) => to, reload: () => undefined };
const EnvContext = createContext<EnvState>(NONE);

/** The environment state; outside the provider (the login page) there are no environments. */
export const useEnv = (): EnvState => useContext(EnvContext);

export function EnvProvider({ tenantId, children }: { tenantId: string; children: ReactNode }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const navType = useNavigationType();
  const seen = useRef(false);
  const stored = useSyncExternalStore(subscribeEnv, getEnvId, () => null);
  const [state, setState] = useState<{ tid: string; envs: Environment[] }>();
  const [failed, setFailed] = useState<string>();

  const load = useCallback(() => {
    Api.environments(tenantId)
      .then((envs) => setState({ tid: tenantId, envs }))
      // Without the list the app still works against the default environment
      .catch(() => setFailed(tenantId));
  }, [tenantId]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 15_000);
    return () => clearInterval(t);
  }, [load]);

  const envs = state?.tid === tenantId ? state.envs : [];
  const ready = state?.tid === tenantId || failed === tenantId;
  const resolved = resolveEnv(stored, envs);
  const settled = ready && resolved === stored;

  // A remembered or linked environment this tenant does not have (or a tenant with one environment): back to the default
  useEffect(() => {
    if (ready && resolved !== stored) setEnvId(resolved);
  }, [ready, resolved, stored]);

  // The URL and the choice stay one: a link naming an environment switches to it, a link that lost it gets it back
  const [lastUrlEnv, setLastUrlEnv] = useState<string | null>(null);
  useEffect(() => {
    if (!settled) return;
    const urlEnv = envOfSearch(loc.search);
    const changed = urlEnv !== lastUrlEnv;
    setLastUrlEnv(urlEnv);
    const here = (search: string) => navigate(`${loc.pathname}${search}${loc.hash}`, { replace: true });
    if (urlEnv && changed && urlEnv !== stored) {
      if (resolveEnv(urlEnv, envs)) setEnvId(urlEnv);
      else here(searchWithEnv(loc.search, null));
    } else if (stored && !urlEnv && changed && navType === 'POP' && seen.current) {
      // Back (or Forward) to an entry written before an environment was chosen: that entry is the default one
      setEnvId(null);
    } else if (stored && urlEnv !== stored) {
      here(searchWithEnv(loc.search, stored));
    }
    seen.current = true;
  }, [settled, loc.pathname, loc.search, loc.hash, stored]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<EnvState>(() => ({
    envs,
    current: envs.find((e) => e.id === stored) ?? envs.find((e) => e.isDefault) ?? null,
    explicitId: stored,
    visible: showSwitcher(envs),
    select: (envId) => void navigate(switchTarget(loc.pathname, loc.search, envId)),
    link: (to) => withEnv(to, stored),
    reload: load,
  }), [envs, stored, navigate, loc.pathname, loc.search, load]);

  if (!settled) return <div className="min-h-screen bg-n1 grid place-items-center"><Spinner /></div>;
  return (
    <EnvContext.Provider value={value}>
      <Fragment key={`${tenantId}:${stored ?? ''}`}>{children}</Fragment>
    </EnvContext.Provider>
  );
}
